import { useState } from "react";

import { getFilteredUpgrades, getGroupedUpgrades } from "libs/item";
import { useSelector } from "react-redux";
import {
  getStorageUpgrades,
  getUpgrades,
} from "redux/selectors/characterSelectors";
import { CharacterDerivedStats, ItemsGroupModel, UpgradeListTabs } from "types";
import {
  CategoryGroup,
  CategoryName,
  CategorySeparator,
} from "./DataScreenBase";
import InventoryUpgradeItem from "./InventoryUpgradeItem";
import { EmptyList, Items, ItemsList, ItemsListWrapper } from "./ItemBase";
import { UpgradeFilter } from "./UpgradeFilter";
import { UpgradeTabs } from "./UpgradeTabs";

type UpgradeInventoryProps = {
  atShipworks?: boolean;
  onHoverUpgrade?: (upgradeSlug: string | null) => void;
};

const TAB_GROUPS: Record<UpgradeListTabs, { name: string; groups: string[] }> =
  {
    available: {
      name: "Available",
      groups: [
        "weapons",
        "shields",
        "thrusters",
        "targetingSystem",
        "reactorCore",
      ],
    },
    storage: {
      name: "Storage",
      groups: ["storage"],
    },
  };

export default function UpgradeInventory({
  atShipworks = false,
  onHoverUpgrade,
}: UpgradeInventoryProps) {
  const upgrades = useSelector(getUpgrades);
  const storage = useSelector(getStorageUpgrades);

  const [filter, setFilter] = useState<keyof CharacterDerivedStats | null>(
    null
  );
  const [selectedTab, setSelectedTab] = useState<UpgradeListTabs>("available");

  const filteredUpgrades = getFilteredUpgrades(upgrades, filter);
  const isEmptyList = filteredUpgrades.length === 0;
  const groupedItems = getGroupedUpgrades(filteredUpgrades, storage);

  const handleFilterChange = (filter: keyof CharacterDerivedStats | null) => {
    setFilter(filter);
  };

  const handleTabChange = (tab: UpgradeListTabs) => {
    setSelectedTab(tab);
  };

  return (
    <ItemsListWrapper>
      <UpgradeTabs selectedTab={selectedTab} onTabChange={handleTabChange} />
      <UpgradeFilter
        selectedFilter={filter}
        onFilterChange={handleFilterChange}
      />
      <ItemsList>
        {Object.entries(groupedItems)
          .filter(([key, value]) => {
            const groupData = value as ItemsGroupModel;
            const tabData = TAB_GROUPS[selectedTab];

            // Only show groups that are in the selected tab and have items
            return tabData.groups.includes(key) && !!groupData.items.length;
          })
          .map(([key, value]) => {
            const groupData = value as ItemsGroupModel;
            return (
              <CategoryGroup key={key}>
                <CategoryName>
                  {groupData.name} <CategorySeparator />
                </CategoryName>
                <Items>
                  {groupData.items.map((itemSlug) => {
                    return (
                      <InventoryUpgradeItem
                        key={itemSlug}
                        slug={itemSlug}
                        atShipworks={atShipworks}
                        onHover={onHoverUpgrade}
                      />
                    );
                  })}
                </Items>
              </CategoryGroup>
            );
          })}
        {isEmptyList && <EmptyList>No Items Found</EmptyList>}
      </ItemsList>
    </ItemsListWrapper>
  );
}
